import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  useGetAccountsQuery,
  useGetBankAccountsQuery,
  useCreateVoucherMutation,
} from '../../../store/api/financeApi';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

function CashBankOperations() {
  const [openDialog, setOpenDialog] = useState(false);
  const [opType, setOpType] = useState(''); // 'deposit-cash', 'withdraw-cash', 'deposit-bank', 'withdraw-bank', 'transfer-bank-cash', 'transfer-cash-bank'
  const [formData, setFormData] = useState({
    date: getLocalDateString(),
    amount: '',
    description: '',
    sourceAccount: '',
    destinationAccount: '',
  });

  const { data: accounts = [] } = useGetAccountsQuery();
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const [createVoucher] = useCreateVoucherMutation();

  const assetAccounts = useMemo(() => {
    return accounts.filter((a) => a.type === 'asset');
  }, [accounts]);

  // Cash accounts: asset accounts that are CASH, or contain 'cash' in name/code, with fallback to other asset accounts
  const cashAccounts = useMemo(() => {
    const specificCash = accounts.filter(
      (a) => a.type === 'asset' && (a.code === 'CASH' || a.name?.toLowerCase().includes('cash') || a.code?.toUpperCase().startsWith('CASH'))
    );
    if (specificCash.length > 0) return specificCash;
    const nonBankAssets = accounts.filter(
      (a) => a.type === 'asset' && a.code !== 'BANK' && !a.code?.toUpperCase().startsWith('BANK')
    );
    if (nonBankAssets.length > 0) return nonBankAssets;
    return accounts.filter((a) => a.type === 'asset');
  }, [accounts]);

  // Bank account options: registered bank accounts + Chart of Accounts bank accounts
  const bankAccountOptions = useMemo(() => {
    const list = [];
    const usedIds = new Set();

    // 1. Registered Bank Accounts from Bank Accounts module
    (bankAccounts || []).forEach((b) => {
      const targetId = b.accountId || b.id;
      if (!targetId) return;
      usedIds.add(targetId);
      if (b.accountId) usedIds.add(b.accountId);
      if (b.id) usedIds.add(b.id);

      const subInfo = [
        b.bankName,
        b.accountNumber ? `Acc #${b.accountNumber}` : null,
        b.account?.code ? `GL: ${b.account.code}` : null,
      ].filter(Boolean).join(' • ');

      list.push({
        id: targetId,
        label: subInfo ? `${b.name} (${subInfo})` : b.name,
      });
    });

    // 2. Chart of Accounts bank accounts (code BANK or name includes Bank) that are not already listed
    (accounts || [])
      .filter((a) => a.type === 'asset' && (a.code === 'BANK' || a.code?.toUpperCase().startsWith('BANK') || a.name?.toLowerCase().includes('bank')))
      .forEach((a) => {
        if (!usedIds.has(a.id)) {
          list.push({
            id: a.id,
            label: `${a.code} - ${a.name} (General Ledger)`,
          });
        }
      });

    // Fallback if empty
    if (list.length === 0) {
      return (accounts || []).filter((a) => a.type === 'asset').map((a) => ({
        id: a.id,
        label: `${a.code} - ${a.name}`,
      }));
    }

    return list;
  }, [bankAccounts, accounts]);
  
  const handleOpen = (type) => {
    setOpType(type);
    setFormData({
      date: getLocalDateString(),
      amount: '',
      description: '',
      sourceAccount: '',
      destinationAccount: '',
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || Number(formData.amount) <= 0) return alert('Enter a valid amount');
    if (!formData.sourceAccount) return alert('Select the source account');
    if (opType.startsWith('deposit') || opType.startsWith('withdraw') || opType.startsWith('transfer')) {
        if (!formData.destinationAccount) return alert('Select the destination account');
    }

    let voucherType = 'journal';
    let lines = [];

    // Map operations to debit/credit
    const amount = Number(formData.amount);
    
    // Deposit Cash: Debit Cash, Credit Source
    if (opType === 'deposit-cash') {
      voucherType = 'receipt';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Cash Account (Asset)
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Income / Other
      ];
    } 
    // Withdraw Cash: Debit Destination, Credit Cash
    else if (opType === 'withdraw-cash') {
      voucherType = 'payment';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Expense / Other
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Cash Account (Asset)
      ];
    }
    // Deposit Bank: Debit Bank, Credit Source
    else if (opType === 'deposit-bank') {
      voucherType = 'receipt';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Bank Account
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Income / Other
      ];
    }
    // Withdraw Bank: Debit Destination, Credit Bank
    else if (opType === 'withdraw-bank') {
      voucherType = 'payment';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Expense / Other
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Bank Account
      ];
    }
    // Transfer Bank to Cash: Debit Cash, Credit Bank
    else if (opType === 'transfer-bank-cash') {
      voucherType = 'journal';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Cash Account
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Bank Account
      ];
    }
    // Transfer Cash to Bank: Debit Bank, Credit Cash
    else if (opType === 'transfer-cash-bank') {
      voucherType = 'journal';
      lines = [
        { accountId: formData.destinationAccount, debit: amount, credit: 0 }, // Bank Account
        { accountId: formData.sourceAccount, debit: 0, credit: amount } // Cash Account
      ];
    }

    try {
      await createVoucher({
        entryDate: formData.date,
        memo: formData.description,
        voucherType,
        lines
      }).unwrap();
      
      alert('Transaction recorded successfully!');
      handleClose();
    } catch (err) {
      alert(err?.data?.message || 'Error recording transaction');
    }
  };

  const getDialogTitle = () => {
    switch (opType) {
      case 'deposit-cash': return 'Add Cash (Deposit to Cashbook)';
      case 'withdraw-cash': return 'Reduce Cash (Withdraw from Cashbook)';
      case 'deposit-bank': return 'Add Bank (Deposit to Bank Ledger)';
      case 'withdraw-bank': return 'Reduce Bank (Withdraw from Bank Ledger)';
      case 'transfer-bank-cash': return 'Transfer: Bank to Cash';
      case 'transfer-cash-bank': return 'Transfer: Cash to Bank';
      default: return 'Transaction';
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        Cash & Bank Operations
      </Typography>

      <Grid container spacing={3}>
        {/* Cash Operations */}
        <Grid size={{xs:12,md:4}}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PaymentsIcon sx={{ fontSize: 48, color: '#4ECDC4', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Cashbook Operations</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button variant="contained" color="success" fullWidth onClick={() => handleOpen('deposit-cash')}>
                  Add Cash (Deposit)
                </Button>
                <Button variant="outlined" color="error" fullWidth onClick={() => handleOpen('withdraw-cash')}>
                  Reduce Cash (Withdraw)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Operations */}
        <Grid size={{xs:12,md:4}}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: '#45b6fe', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Bank Ledger Operations</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button variant="contained" color="success" fullWidth onClick={() => handleOpen('deposit-bank')}>
                  Add Bank (Deposit)
                </Button>
                <Button variant="outlined" color="error" fullWidth onClick={() => handleOpen('withdraw-bank')}>
                  Reduce Bank (Withdraw)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Transfer Operations */}
        <Grid size={{xs:12,md:4}}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SwapHorizIcon sx={{ fontSize: 48, color: '#9c27b0', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Transfers</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button variant="outlined" color="primary" fullWidth onClick={() => handleOpen('transfer-bank-cash')}>
                  Transfer: Bank to Cash
                </Button>
                <Button variant="outlined" color="secondary" fullWidth onClick={() => handleOpen('transfer-cash-bank')}>
                  Transfer: Cash to Bank
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="tx-form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            
            {/* Amount & Date */}
            <Grid container spacing={2}>
              <Grid size={{xs:12,sm:6}}>
                <TextField
                  label="Amount"
                  type="number"
                  fullWidth
                  required
                  inputProps={{ step: 0.01, min: 0 }}
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                />
              </Grid>
              <Grid size={{xs:12,sm:6}}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </Grid>
            </Grid>

            {/* Source Account */}
            <TextField
              select
              label={
                opType.includes('withdraw') ? (opType.includes('cash') ? 'From Cash Account' : 'From Bank Account') 
                : opType.includes('deposit') ? 'From Source Account (Income/Other)'
                : opType === 'transfer-bank-cash' ? 'From Bank Account'
                : 'From Cash Account'
              }
              fullWidth
              required
              value={formData.sourceAccount}
              onChange={(e) => setFormData({ ...formData, sourceAccount: e.target.value })}
            >
              {
                (opType === 'withdraw-cash' || opType === 'transfer-cash-bank') ? (
                  cashAccounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.code} - {a.name}
                    </MenuItem>
                  ))
                ) : (opType === 'withdraw-bank' || opType === 'transfer-bank-cash') ? (
                  bankAccountOptions.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.label}
                    </MenuItem>
                  ))
                ) : (
                  accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.code} - {a.name} ({a.type})
                    </MenuItem>
                  ))
                )
              }
            </TextField>

            {/* Destination Account */}
            <TextField
              select
              label={
                opType.includes('deposit') ? (opType.includes('cash') ? 'To Cash Account' : 'To Bank Account')
                : opType.includes('withdraw') ? 'To Destination Account (Expense/Other)'
                : opType === 'transfer-bank-cash' ? 'To Cash Account'
                : 'To Bank Account'
              }
              fullWidth
              required
              value={formData.destinationAccount}
              onChange={(e) => setFormData({ ...formData, destinationAccount: e.target.value })}
            >
              {
                (opType === 'deposit-cash' || opType === 'transfer-bank-cash') ? (
                  cashAccounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.code} - {a.name}
                    </MenuItem>
                  ))
                ) : (opType === 'deposit-bank' || opType === 'transfer-cash-bank') ? (
                  bankAccountOptions.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.label}
                    </MenuItem>
                  ))
                ) : (
                  accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.code} - {a.name} ({a.type})
                    </MenuItem>
                  ))
                )
              }
            </TextField>

            {/* Description */}
            <TextField
              label="Description / Memo"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" form="tx-form" variant="contained" color="primary">
            Record Transaction
          </Button>
        </Box>
      </Dialog>

    </Box>
  );
}

export default CashBankOperations;

